import { Spinner } from '@blueprintjs/core';
import * as React from 'react';

import {
  DataSourceTable,
  DataSourceTableSelectItem,
} from 'src/constants/dataSourceTable';
import { ProjectVirtualGraphToCreate } from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/types';
import { ProjectVirtualGraphPanelScope } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanelScope';
import { getCustomQueryFromVgsToCreate } from 'src/containers/ProjectVirtualGraphPanel/utils/getCustomQueryFromVgsToCreate';
import { DataSource } from 'src/store/state/dataSources';
import { generateDateRandomId } from 'src/utils/generateId';

type QueueItem = {
  from: DataSourceTableSelectItem | string;
  virtualGraphId: string;
};

export type ProjectVirtualGraphPanelExistingScopeContainerProps = {
  addItemToQueue: (isExisting?: boolean, ...queueItems: QueueItem[]) => void;
  dataSource: DataSource | null;
  handleRemoveVirtualGraph?: (id: string) => void;
  isPendingTables?: boolean;
  isUsingCustomQuery: boolean;
  setIsUsingCustomQuery: (isUsingCustomQuery: boolean) => void;
  showError: boolean;
  tables: DataSourceTable[];
  virtualGraph: ProjectVirtualGraphToCreate;
};

export const ProjectVirtualGraphPanelExistingScopeContainer: React.VFC<
  ProjectVirtualGraphPanelExistingScopeContainerProps
> = ({
  addItemToQueue,
  dataSource,
  handleRemoveVirtualGraph,
  isPendingTables,
  isUsingCustomQuery,
  setIsUsingCustomQuery,
  showError,
  tables,
  virtualGraph,
}) => {
  const [customQueryText, setCustomQueryText] = React.useState<string>(
    (getCustomQueryFromVgsToCreate({ [virtualGraph.id]: virtualGraph })
      ?.from as string) || ''
  );

  const handleSelectTable = React.useCallback(
    (tableItem: DataSourceTableSelectItem) => {
      if (typeof virtualGraph.from !== 'object') {
        return;
      }

      // Do nothing if item is already selected
      if (virtualGraph.from.name === tableItem.name) {
        handleRemoveVirtualGraph?.(virtualGraph.id as string);
        return;
      }
      addItemToQueue(true, {
        virtualGraphId: generateDateRandomId(),
        from: tableItem,
      });
    },
    [addItemToQueue, handleRemoveVirtualGraph, virtualGraph]
  );

  const handleCustomQueryTextChange = React.useCallback((customText) => {
    setCustomQueryText(customText);
  }, []);

  const validateCustomQuery = React.useCallback(
    (text: string) => {
      addItemToQueue(true, {
        from: text,
        virtualGraphId: virtualGraph.id,
      });
    },
    [addItemToQueue, virtualGraph.id]
  );
  return isPendingTables ? (
    <Spinner className="sd-project-virtual-graph-panel__resource-scope" />
  ) : (
    <ProjectVirtualGraphPanelScope
      customQueryText={customQueryText}
      handleCustomQueryTextChange={handleCustomQueryTextChange}
      handleIsUsingCustomQueryChange={setIsUsingCustomQuery}
      handleRemoveVirtualGraph={handleRemoveVirtualGraph}
      handleSelectTable={handleSelectTable}
      isForSingleTable
      isUsingCustomQuery={isUsingCustomQuery}
      showError={showError}
      tableMetadataErrorResponse={
        dataSource?.tableMetadataErrorResponse || null
      }
      tables={tables}
      validateCustomQuery={validateCustomQuery}
      virtualGraphsToCreate={{ [virtualGraph.id]: virtualGraph }}
    />
  );
};
